import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { createChart } from 'lightweight-charts';
import './OptionsLiveChart.css';

const TF_SECONDS = { '1min': 60, '5min': 300, '15min': 900 };
const IST_TZ = 'Asia/Kolkata';

function toIST(ts) {
  return new Date(ts * 1000).toLocaleTimeString('en-IN', { timeZone: IST_TZ, hour12: false });
}
function fmtCountdown(s) {
  if (s < 0) return '00:00';
  const m = Math.floor(s / 60), sec = s % 60;
  return String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
}
function candleSecondsRemaining(lastBarTime, tf) {
  if (!lastBarTime) return 0;
  const period = TF_SECONDS[tf] || 60;
  const candleEnd = lastBarTime + period;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, candleEnd - now);
}

const ChartPanel = forwardRef(({ n, onAction }, ref) => {
  const chartContainerRef = useRef();
  const chartInstanceRef = useRef(null);
  const seriesRef = useRef(null);

  const [mode, setMode] = useState('option');
  const [sym, setSym] = useState('—');
  const [tf, setTf] = useState('1min');
  
  // Form inputs for option mode
  const [und, setUnd] = useState('SPY');
  const [expiry, setExpiry] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });
  const [type, setType] = useState('C');
  const [strike, setStrike] = useState('560');

  // Form inputs for spot mode
  const [spotSym, setSpotSym] = useState('SPX');

  // Display state
  const [price, setPrice] = useState(0);
  const [firstPrice, setFirstPrice] = useState(0);
  const [lastBarTime, setLastBarTime] = useState(0);
  const [tickTs, setTickTs] = useState('');
  const [ohlc, setOhlc] = useState(null);

  const stateRef = useRef({ firstPrice: 0, lastBarTime: 0, tf: '1min', mode: 'option' });

  useImperativeHandle(ref, () => ({
    handleMsg: (data) => {
      const st = stateRef.current;
      if (data.type === 'historical') {
        const bars = (data.bars || []).map(b => ({ time: +b.time, open: +b.open, high: +b.high, low: +b.low, close: +b.close })).sort((a, b) => a.time - b.time);
        if (bars.length) {
          st.firstPrice = bars[0].open;
          setFirstPrice(bars[0].open);
          st.lastBarTime = bars[bars.length - 1].time;
          setLastBarTime(st.lastBarTime);
          seriesRef.current?.setData(bars);
          chartInstanceRef.current?.timeScale().fitContent();
          setPrice(bars[bars.length - 1].close);
        } else {
          seriesRef.current?.setData([]);
        }
      } else if (data.type === 'candle_update') {
        const b = data.bar;
        if (b) {
          const bar = { time: +b.time, open: +b.open, high: +b.high, low: +b.low, close: +b.close };
          seriesRef.current?.update(bar);
          st.lastBarTime = bar.time;
          setLastBarTime(bar.time);
          setPrice(bar.close);
        }
      } else if (data.type === 'tick_update') {
        const p = +data.price;
        if (p > 0) {
          setPrice(p);
          const timeStr = data.timestamp ? toIST(data.timestamp) : new Date().toLocaleTimeString('en-IN', { timeZone: IST_TZ, hour12: false });
          setTickTs(timeStr);
        }
      }
    }
  }));

  useEffect(() => {
    const lc = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      layout: { backgroundColor: '#131722', textColor: '#d1d4dc', fontSize: 11 },
      grid: { vertLines: { color: '#1f2431' }, horzLines: { color: '#1f2431' } },
      crosshair: { mode: 0 },
      rightPriceScale: { borderColor: '#2a2e39' },
      localization: {
        timeFormatter: timestamp => {
          return new Intl.DateTimeFormat('en-IN', {
            timeZone: IST_TZ,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          }).format(new Date(timestamp * 1000));
        }
      },
      timeScale: {
        borderColor: '#2a2e39', timeVisible: true, secondsVisible: false,
        tickMarkFormatter: t => {
          return new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(t * 1000));
        }
      }
    });
    
    const series = lc.addCandlestickSeries({
      upColor: '#089981', downColor: '#f23645', borderVisible: false, wickUpColor: '#089981', wickDownColor: '#f23645',
    });
    
    lc.subscribeCrosshairMove(param => {
      if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
        setOhlc(null);
        return;
      }
      const data = param.seriesPrices.get(series);
      if (data) {
        setOhlc({ o: data.open.toFixed(2), h: data.high.toFixed(2), l: data.low.toFixed(2), c: data.close.toFixed(2) });
      } else {
        setOhlc(null);
      }
    });

    chartInstanceRef.current = lc;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      if (chartContainerRef.current) {
        lc.applyOptions({ width: chartContainerRef.current.clientWidth, height: chartContainerRef.current.clientHeight });
      }
    });
    ro.observe(chartContainerRef.current);

    return () => {
      ro.disconnect();
      lc.remove();
    };
  }, []);

  const buildOSI = (u, e, t, s) => {
    let root = (u || 'SPY').trim().toUpperCase();
    if (root === 'SPX') root = 'SPXW';
    let dateStr = '260928';
    if (e) {
      const p = e.split('-');
      if (p.length === 3) dateStr = p[0].slice(2) + p[1] + p[2];
    }
    const typeChar = (t || 'C').toUpperCase().startsWith('P') ? 'P' : 'C';
    const strikeVal = Math.round((parseFloat(s) || 765) * 1000);
    return root + dateStr + typeChar + String(strikeVal).padStart(8, '0');
  };

  const handleLoad = () => {
    setFirstPrice(0);
    setLastBarTime(0);
    setTickTs('');
    stateRef.current.firstPrice = 0;
    stateRef.current.lastBarTime = 0;
    stateRef.current.tf = tf;
    stateRef.current.mode = mode;
    seriesRef.current?.setData([]);

    if (mode === 'spot') {
      const s = (spotSym || 'SPX').toUpperCase();
      setSym(s + ' SPOT');
      onAction({ action: 'subscribe_spot', chart_id: `chart${n}`, symbol: s, timeframe: tf });
    } else {
      const osi = buildOSI(und, expiry, type, strike);
      setSym(osi);
      onAction({ action: 'subscribe', chart_id: `chart${n}`, symbol: osi, timeframe: tf });
    }
  };

  const [countdown, setCountdown] = useState({ text: '⏱ 00:00', cls: 'ok' });
  useEffect(() => {
    const timer = setInterval(() => {
      if (lastBarTime > 0) {
        const rem = candleSecondsRemaining(lastBarTime, tf);
        const pct = rem / (TF_SECONDS[tf] || 60);
        setCountdown({
          text: '⏱ ' + fmtCountdown(rem),
          cls: pct <= 0.10 ? 'urgent' : pct <= 0.25 ? 'soon' : 'ok'
        });
      } else {
        setCountdown({ text: '⏱ —', cls: 'ok' });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [lastBarTime, tf]);

  const pctChange = firstPrice > 0 ? ((price - firstPrice) / firstPrice) * 100 : 0;
  const sign = pctChange >= 0 ? '+' : '';
  const priceColor = pctChange >= 0 ? 'var(--green)' : 'var(--red)';

  return (
    <div className="chart-panel" id={`panel${n}`}>
      <div className="mode-bar">
        <button className={`mode-btn ${mode === 'option' ? 'active' : ''}`} data-mode="option" onClick={() => setMode('option')}>OPTION</button>
        <button className={`mode-btn ${mode === 'spot' ? 'active' : ''}`} data-mode="spot" onClick={() => setMode('spot')}>SPOT</button>
      </div>

      {mode === 'option' && (
        <div className="controls-bar" id={`opt-controls${n}`}>
          <div className="ctrl-group ctrl-underlying">
            <label>UND</label>
            <input type="text" value={und} onChange={e => setUnd(e.target.value)} />
          </div>
          <div className="ctrl-group ctrl-expiry">
            <label>EXPIRY</label>
            <input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
          </div>
          <div className="ctrl-group ctrl-type">
            <label>TYPE</label>
            <select value={type} onChange={e => setType(e.target.value)}>
              <option value="C">CALL</option>
              <option value="P">PUT</option>
            </select>
          </div>
          <div className="ctrl-group ctrl-strike">
            <label>STRIKE</label>
            <input type="number" value={strike} onChange={e => setStrike(e.target.value)} />
          </div>
          <div className="ctrl-group">
            <label>TF</label>
            <div className="tf-group">
              {['1min', '5min', '15min'].map(t => (
                <button key={t} className={`tf ${tf === t ? 'active' : ''}`} onClick={() => setTf(t)}>{t.replace('min','m')}</button>
              ))}
            </div>
          </div>
          <div className="ctrl-actions">
            <button className="btn-load" onClick={handleLoad}>LOAD</button>
          </div>
        </div>
      )}

      {mode === 'spot' && (
        <div className="controls-bar spot-controls" id={`spot-controls${n}`}>
          <div className="ctrl-group ctrl-spot-sym">
            <label>SYMBOL</label>
            <input type="text" value={spotSym} onChange={e => setSpotSym(e.target.value)} />
          </div>
          <div className="ctrl-group">
            <label>TF</label>
            <div className="tf-group">
              {['1min', '5min', '15min'].map(t => (
                <button key={t} className={`tf ${tf === t ? 'active' : ''}`} onClick={() => setTf(t)}>{t.replace('min','m')}</button>
              ))}
            </div>
          </div>
          <div className="ctrl-actions">
            <button className="btn-load spot" onClick={handleLoad}>LOAD</button>
          </div>
        </div>
      )}

      <div className="symbol-bar">
        <span className={`chart-label ch${n}`}>CHART {n}</span>
        <span className={`mode-badge ${mode === 'spot' ? 'spot' : ''}`} id={`modebadge${n}`}>{mode.toUpperCase()}</span>
        <span className="symbol-text">{sym}</span>
        <span className={`price-display ${pctChange < 0 ? 'down' : ''}`}>${price.toFixed(2)}</span>
        <span className="change-display" style={{ color: priceColor }}>{sign}{pctChange.toFixed(2)}%</span>
        <span className="tick-timestamp">{tickTs ? ` | ${tickTs} IST` : ''}</span>
        <span className={`candle-countdown ${countdown.cls}`}>{countdown.text}</span>
      </div>

      <div className="chart-canvas" ref={chartContainerRef}>
        <div className="ohlc-legend">
          {ohlc && (
            <>
              <span><b>O</b> <span className="ohlc-val">{ohlc.o}</span></span>
              <span><b>H</b> <span className="ohlc-val">{ohlc.h}</span></span>
              <span><b>L</b> <span className="ohlc-val">{ohlc.l}</span></span>
              <span><b>C</b> <span className="ohlc-val">{ohlc.c}</span></span>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default ChartPanel;
